import { ContextMenu, Li, Button, Dialog, Loading, Icon } from '@startlibs/components';
import { formatDate, getColor } from '@startlibs/utils';
import { lighten } from 'polished';
import {useToggle, usePopupToggle, willUseSharedCallback} from '@startlibs/core'
import React, { forwardRef } from 'react';
import _ from 'lodash/fp';
import styled from 'styled-components';

import {
  AttachmentActions,
  AttachmentBox,
  AttachmentDescription,
  AttachmentIcon,
  AttachmentInfoContainer,
  DropdownButton,
  DropdownIcon,
  ProgressBar,
  ProgressDetails,
  TextButton,
  UploadedDate,
  UploadingStatus
} from './AttachmentBoxStyles';
import {useDownload} from '../utils/downloadFile'
import {useUploader} from '../hooks/useUploader'
import {formatDateNoUTC} from '../utils'

const [useRegisterCancel,callCancels] = willUseSharedCallback()

export const NonCompliantBox = ({disabled, jwt, appJwt, onFinishUpload, onCancel, onFailure, retry, removeFromQueue, removeAttachment, removeNonCompliantFile, attachments,isApp, withoutDelete,
  dragProvided
}) => {
  const detailsDialog = useToggle()
  const cancelDialog = useToggle()
  const deleteDialog = useToggle()
  const loading = useToggle()
  const contextMenu = usePopupToggle()

  const uploadingInstances = attachments.filter(instance => instance.fileToUpload)
  const latestInstance = _.maxBy(({uploadDate})=> uploadDate || 0,attachments)

  const queuedInstances = attachments.filter(instance => instance.sourceFile)
  const doneInstances = attachments.filter(instance => !instance.sourceFile)
  const failedInstances = attachments.filter(instance => instance.failed)
  const hasNewFiles = !!attachments.find(instance => instance.isNew)

  const uploaded = doneInstances.length
  const total = attachments.length

  const progress = uploaded/total
  const isFinished = uploaded === total

  const cancelQueue = () => {
    removeFromQueue()
    callCancels()
    cancelDialog.close()
  }

  const cancelAndRemove = () => {
    cancelQueue()
    return removeAttachment([])
  }

  const deleteFile = (f) => {
    return removeNonCompliantFile(f)
  }

  const retryAll = () => {
    failedInstances.forEach((instance) => retry(instance)())
  }

  const hasOnlyFailedFiles = queuedInstances.length === failedInstances.length && failedInstances.length > 0

  const [tryDownload,downloadIsLoading,downloadIsStating,downloadFailed] = useDownload(appJwt,{files:attachments.map(f => f.uid + "." + f.fileExtension)})

  return <AttachmentBox
    failedUpload={hasOnlyFailedFiles} isApp={isApp}
    ref={dragProvided.innerRef}
    {...dragProvided.draggableProps}
    {...dragProvided.dragHandleProps}
  >
    <AttachmentIcon icon="dicom" css="font-size:44px;"/>
    {
      !isFinished && (hasOnlyFailedFiles ?
      <UploadingStatus>
      {uploaded > 0 && <ProgressDetails>{uploaded}/{total} files successfully uploaded</ProgressDetails>}
      <ProgressDetails failed>
        <a className="link error" onClick={detailsDialog.open}>{failedInstances.length}/{total} file upload{failedInstances.length > 1 && 's'} failed</a>
        <a className="link" onClick={retryAll}>Retry{failedInstances.length > 1 && ' all'}</a>
      </ProgressDetails>
      </UploadingStatus>
      :
      <UploadingStatus>
        <ProgressDetails>
          <span>Uploading files...</span><a className="link" onClick={cancelDialog.open}>Cancel</a>
        </ProgressDetails>
        <ProgressBar progress={progress * 100} completed={isFinished}/>
        {failedInstances.length > 0 ?
          <div className="files-count"><a className="link error" onClick={detailsDialog.open}>{failedInstances.length} file upload{failedInstances.length > 1 && 's'} failed</a> <a className="link" onClick={retryAll}>Retry{failedInstances.length > 1 && ' all'}</a></div>
          :
          <div className="files-count"><a className="link" onClick={detailsDialog.open}>{uploaded}/{total} files uploaded</a></div>
        }
      </UploadingStatus>
    )}
    <AttachmentInfoContainer>
      <AttachmentDescription>
        <b>{total} Non compliant DICOM files <a className="link" onClick={detailsDialog.open}>Details</a></b>
      </AttachmentDescription>
    </AttachmentInfoContainer>
    <AttachmentActions>
      <div className="buttons-wrapper">
      {
        isFinished && !isApp &&
        <TextButton isLoading={downloadIsLoading} onClick={tryDownload}>
          {
            downloadIsLoading ?
            <><Loading size={15} borderWidth={3}/>Preparing...</>
            :
            downloadIsStating ?
            <><Icon icon="check"/>Started</>
            :
            <><Icon icon="download"/>Download</>
          }
        </TextButton>
      }
      {
        isFinished && !disabled && !withoutDelete &&
        <DropdownButton onClick={contextMenu.open}>
          <DropdownIcon icon="arrow-down"/>
          {
            contextMenu.isOpen &&
            <ContextMenu>
              <Li label="Delete non compliant files" icon="delete" onClick={deleteDialog.open}/>
            </ContextMenu>
          }
        </DropdownButton>
      }
      </div>
      {
        latestInstance.uploadDate && <UploadedDate>
          Uploaded: {formatDateNoUTC(new Date(latestInstance.uploadDate),"MM/dd/yyyy - hh:mm")}
        </UploadedDate>
      }
    </AttachmentActions>
    {
      detailsDialog.isOpen &&
      <DetailsDialog
        closeDialog={detailsDialog.close}
        doneInstances={doneInstances}
        queuedInstances={queuedInstances}
        uploadingInstances={uploadingInstances}
        cancelDialog={cancelDialog}
        attachments={attachments}
        retry={retry}
        readOnly={disabled}
        withoutDelete={withoutDelete}
        uploaded={uploaded}
        total={total}
        progress={progress}
        deleteFile={deleteFile}
        retryAll={failedInstances.length > 0 && retryAll}
        failedInstances={failedInstances}
        isFinished={isFinished}
        hasOnlyFailedFiles={hasOnlyFailedFiles}
        hasNewFiles={hasNewFiles}
      />
    }
    {uploadingInstances.map(instance =>
      <UploadInstance
        jwt={jwt}
        key={instance.instanceUID || instance.key}
        instance={instance}
        onCancel={onCancel(instance)}
        onFinishUpload={onFinishUpload(instance)}
        onFailure={onFailure(instance)}
      />
    )}

    {
      cancelDialog.isOpen &&
      <Dialog
        closeDialog={cancelDialog.close}
        title="Cancel non compliant DICOM upload"
        footer={<>
          <Button alert isLoading={loading.isOpen} onClick={loading.willWait(cancelAndRemove)}>Delete all non compliant files</Button>
          <Button alert onClick={cancelQueue}>Stop remaining uploads only</Button>
        </>}
      >
      <p>Would you like to cancel only the remaining file uploads in queue or also delete all non compliant DICOM files?</p>
      </Dialog>
    }
    {
      deleteDialog.isOpen &&
      <Dialog
        closeDialog={deleteDialog.close}
        title="Delete all non compliant files"
        footer={<>
          <Button onClick={deleteDialog.close}>Cancel</Button>
          <Button alert isLoading={loading.isOpen} onClick={loading.willWait(cancelAndRemove)}>Delete non compliant files</Button>
        </>}
      >
      <p>Would you like to delete all non compliant DICOM files already uploaded?</p>
      <p>Once confirmed this cannot be undone.</p>
      </Dialog>
    }

  </AttachmentBox>
}

const FilesListHeader = styled.div `
  display: flex;
  justify-content: space-between;
`
const FilesList = styled.div `
  border-radius: 5px;
  height: 16rem;
  border: 1px solid ${getColor('gray210')};
  overflow: hidden;
  > div {
    overflow: auto;
    height: calc(100% + 1px);
  }
`
const FailedUploads = styled.div `
  color: ${getColor('alert')};
  margin-top: 1rem;
  font-weight: 600;
  margin-bottom: -1rem;
  background: ${props => lighten(0.51, props.theme.colors.alert)};
  border: 1px solid #e09f9f;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  ${Icon} {
    font-size: 18px;
    vertical-align: -4px;
    margin-right: 0.5rem;
  }
`

const DetailsDialog = ({hasNewFiles, readOnly, closeDialog, cancelDialog, attachments, deleteFile, retry, retryAll, uploadingInstances, queuedInstances, doneInstances, uploaded, total, progress, failedInstances, isFinished, hasOnlyFailedFiles, withoutDelete}) => {
  return <Dialog
    title="Non compliant DICOM file details"
    closeDialog={closeDialog}
    footer={<Button onClick={closeDialog}>Close</Button>}
  >
  <p>
    Some DICOM files were incompatible with our webviewer. They are grouped together here and will be available for the expert to download them.
  </p>
    { !readOnly && hasNewFiles && <>
      { isFinished ?
        <FilesListHeader><div><b>Upload completed</b> ({uploaded}/{total} files uploaded)</div></FilesListHeader>
        : (hasOnlyFailedFiles ?
        <FilesListHeader><div><b>Files uploaded</b> ({uploaded}/{total})</div><a className="link" onClick={cancelDialog.open}>Cancel</a></FilesListHeader>
        :
        <FilesListHeader><div><b>Uploading files</b> ({uploaded}/{total} uploaded)</div><a className="link" onClick={cancelDialog.open}>Cancel upload</a></FilesListHeader>
        )
      }
      <ProgressBar progress={progress * 100} completed={isFinished} failed={hasOnlyFailedFiles} css="margin: 0.5rem 0;"/>
      </>
    }
    <FilesList>
      <div>
      {attachments.map(file =>
        <InstanceRow
          key={file.instanceUID || file.uid}
          file={file}
          retry={retry}
          deleteFile={deleteFile}
          isUploading={uploadingInstances.indexOf(file) >= 0}
          isQueued={queuedInstances.indexOf(file) >= 0}
          isDone={doneInstances.indexOf(file) >= 0}
          readOnly={readOnly}
          withoutDelete={withoutDelete}
        />
      )}
      </div>
    </FilesList>
    {retryAll && <FailedUploads><Icon icon="failure"/>{failedInstances.length} {failedInstances.length > 1 ? "file uploads have failed." : "file upload has failed."} <a className="link" onClick={retryAll}>Retry{failedInstances.length > 1 && ' all'}</a></FailedUploads>}
  </Dialog>
}

const InstanceRow = styled(({readOnly, withoutDelete,file, isUploading, isQueued, retry, className, deleteFile}) => {

  const deleteDialog = useToggle()
  const loading = useToggle()

  return <div className={className}>
    <div className="file-details">
      <div><strong>{file.fileName}</strong></div>
    </div>
    { !readOnly &&
      <div className="right-info">
        { file.isNew ?
          (
            file.failed
              ? (file.failed === "filename"
                ? <span className="failed">Invalid filename. Cannot upload</span>
                : <span className="failed">Upload failed <a className="link" onClick={retry(file)}>Retry</a></span>
              )
              : isUploading
              ? <span><b><Loading size={12} borderWidth={2}/>Uploading</b></span>
              : isQueued
                ? <span>Waiting</span>
                : (!withoutDelete && <span>Uploaded <a className="link" onClick={deleteDialog.open}>Delete</a></span>)
          )
          :
          (!withoutDelete && <span><a className="link" onClick={deleteDialog.open}>Delete</a></span>)
        }
      </div>
    }

    {
      deleteDialog.isOpen &&
      <Dialog
        closeDialog={deleteDialog.close}
        title="Delete non compliant file"
        footer={<>
          <Button onClick={deleteDialog.close}>Cancel</Button>
          <Button alert isLoading={loading.isOpen} onClick={loading.willWait(() => deleteFile(file))}>Delete non compliant file</Button>
        </>}
      >
        <p>You are about to delete the following file:</p>
        <AttachmentBox nonCompliantDeleteFileBox css="margin-bottom:1rem;">
          <AttachmentIcon icon="dicom" css="font-size:44px;" className="desktop-only"/>
          <AttachmentInfoContainer>
          <div className="file-details">
            <div><strong>{file.fileName}</strong></div>
          </div>
        </AttachmentInfoContainer>
      </AttachmentBox>
      <p><b>Are you sure you want to delete it?</b> Once confirmed this cannot be undone.</p>
      </Dialog>
    }
  </div>
})`
  padding: 1rem;
  border-bottom: 1px solid ${getColor('gray210')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  span {
    flex-shrink: 0;
    margin-left: 1.5rem;
  }
  .failed {
    color: ${getColor('alert')};
  }
  ${Loading} {
    display: inline-block;
    margin-right: 4px;
    vertical-align: -1px;
  }
  .file-details {
    margin-right: .5rem;
    flex-grow: 1;
  }
  .right-info {
    span {
      margin-left: 0;
    }
  }
  .link {
    margin-left: .25rem;
  }
`

const UploadInstance = forwardRef(({instance, jwt, onFinishUpload, onCancel, onFailure}, ref) => {


  // const onCancel = console.log
  // const onFailure = console.log

  const params = {fileType: "DICOM"}


  const [progress, cancel] = useUploader(instance.fileToUpload, {
    jwt,
    params,
    onSuccess:onFinishUpload,
    onCancel,
    onFailure
  })

  useRegisterCancel((i) => {
    cancel()
  })

  return null

})
